'use client';
import { ElementType, PropsWithChildren, useState } from 'react';
import { cn } from '@/lib/utils';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { deleteCookie, getCookie } from 'cookies-next';

import PowerIcon from '../Icons/Admin/Power';
import ProfileIcon from '../Icons/Admin/Profile';
import FlagIcon from '../Icons/Admin/Flag';
import DocumentsIcon from '../Icons/Admin/Documents';
import WalletIcon from '../Icons/Admin/Wallet';
import BalloonIcon from '../Icons/Admin/Balloon';
import VillageIcon from '../Icons/Admin/Village';
import DashboardIcon from '../Icons/Admin/Dashboard';
import CalendarBookingsIcon from '../Icons/Admin/CalendarBookings';
import DialogBoxIcon from '../Icons/Admin/DialogBox';

import useClickOutside from '@/hooks/useClickOutside';
import useUserContext from '@/hooks/useUserContext';

import { IUserDropDownDictionary } from '@/dictionaries/components/userDropDown/types';
import { DEV_URL } from '@/utils/url';

type Props = {
  lang: string;
  dict: IUserDropDownDictionary;
} & PropsWithChildren;

type ListItemProps = {
  label: string;
  href: string;
  icon: ElementType;
  onClick?: () => void;
};

function ListItem({ label, href, icon: Icon, onClick }: ListItemProps) {
  return (
    <li
      onClick={onClick}
      className="cursor-pointer leading-[18px] relative whitespace-nowrap hover:bg-gray-300 hover:transition-all hover:duration-100 hover:ease-out">
      <Link
        target="_blank"
        href={href}
        className="flex items-center py-[8px] px-[16px] w-full">
        <Icon className="w-6 h-[24px] text-blue mr-4" />
        <span>{label}</span>
      </Link>
    </li>
  );
}

export default function UserDropDown({ lang, dict, children }: Props) {
  const [dropDownIsOpen, setDropDownIsOpen] = useState<boolean>(false);
  const { userGrant, logout: contextLogout } = useUserContext();
  const { admin, owner, customer, logout, logoutHref } = dict;

  const pathname = usePathname();

  const sessionId = getCookie('sessionId');

  const ref = useClickOutside({
    clickFn: () => setDropDownIsOpen(false),
  });

  async function handleLogout() {
    await contextLogout();
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  }

  return (
    <div
      ref={ref}
      onClick={() => setDropDownIsOpen(!dropDownIsOpen)}
      className="w-10 h-[40px] relative whitespace-nowrap inline-flex items-center uppercase text-sm cursor-pointer">
      {children}
      <ul
        className={cn(
          'z-[999] w-auto absolute pt-[16px] px-[8px] pb-[12px] bg-white shadow-[0_1px_10px_0_rgba(0,0,0,.2)] top-[120%] right-0 min-w-[260px] list-none text-gray-900',
          {
            hidden: !dropDownIsOpen,
          },
        )}>
        {userGrant == 'admin' && (
          <ListItem
            href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
              admin.dashboardHref,
            )}`}
            label={admin.dashboard}
            icon={DashboardIcon}
          />
        )}
        {userGrant == 'owner' && (
          <>
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                owner.dashboardHref,
              )}`}
              label={owner.dashboard}
              icon={DashboardIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                owner.bookingsHref,
              )}`}
              label={owner.bookings}
              icon={CalendarBookingsIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                owner.feedbackHref,
              )}`}
              label={owner.feedback}
              icon={DialogBoxIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                owner.yourPaymentsHref,
              )}`}
              label={owner.yourPayments}
              icon={WalletIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                owner.travelDocumentsHref,
              )}`}
              label={owner.travelDocuments}
              icon={DocumentsIcon}
            />
          </>
        )}
        {userGrant == 'customer' && (
          <>
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                customer.dashboardHref,
              )}`}
              label={customer.dashboard}
              icon={DashboardIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                customer.villagesHref,
              )}`}
              label={customer.villages}
              icon={VillageIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                customer.experiencesHref,
              )}`}
              label={customer.experiences}
              icon={BalloonIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                customer.yourPaymentsHref,
              )}`}
              label={customer.yourPayments}
              icon={WalletIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                customer.travelDocumentsHref,
              )}`}
              label={customer.travelDocuments}
              icon={DocumentsIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                customer.planYourTripHref,
              )}`}
              label={customer.planYourTrip}
              icon={FlagIcon}
            />
            <ListItem
              href={`${DEV_URL}/?token=${sessionId}&url=${encodeURIComponent(
                customer.profileHref,
              )}`}
              label={customer.profile}
              icon={ProfileIcon}
            />
          </>
        )}
        <ListItem
          onClick={handleLogout}
          href={pathname}
          label={logout}
          icon={PowerIcon}
        />
      </ul>
    </div>
  );
}
